/**
 * Cookie consent client helper.
 *
 * Source of truth for "is the user OK with this category?". Reads from
 * ``localStorage[STORAGE_KEY]`` (v2 format introduced in PR 05 of the
 * GDPR/ISO27001/NIS2 readiness plan); falls back to the v1 sentinel
 * ``"accepted"`` / ``"declined"`` so a returning visitor isn't
 * re-prompted unnecessarily — but the v1 ``"accepted"`` value
 * intentionally does NOT auto-grant analytics/marketing because the
 * banner that wrote it didn't differentiate.
 *
 * The banner POSTs each decision to ``/api/v1/consent`` so the
 * server-side ledger (PR 04) is authoritative for audit purposes;
 * this helper is only the pre-render gate (e.g. for
 * ``initSentry()``).
 */

export const CONSENT_STORAGE_KEY = "dxf_cookie_consent_v2";
export const CONSENT_LEGACY_STORAGE_KEY = "dxf_cookie_consent_v1";

/**
 * Událost, na kterou cookie lišta znovu vyjede i po uložené volbě
 * (KAN-1939). Spouští ji „Nastavení cookies" v patičce — souhlas musí
 * jít změnit a odvolat stejně snadno, jako šel udělit.
 */
export const OPEN_COOKIE_SETTINGS_EVENT = "forgmatic:open-cookie-settings";

export function openCookieSettings(): void {
  window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT));
}
/**
 * Fallback verze politiky, když shell nemá publikovaný dokument.
 *
 * Dřív to byla JEDINÁ hodnota a ukazovala na dokument, který
 * neexistoval — ledger tvrdil „souhlas s v1", ale žádné v1 nikde
 * nebylo. Od zavedení právních dokumentů se posílá verze SKUTEČNĚ
 * zobrazeného znění (viz ``CookieBanner``); tohle zůstává jen pro
 * případ, kdy ještě nic publikované není.
 */
const CONSENT_POLICY_FALLBACK = "v1";

type ConsentCategory = "essential" | "analytics" | "marketing";

interface ConsentPrefs {
  essential: true; // always granted; type alias for clarity
  analytics: boolean;
  marketing: boolean;
  version: string;
  ts: string;
}

const DEFAULT_PREFS: Omit<ConsentPrefs, "ts"> = {
  essential: true,
  analytics: false,
  marketing: false,
  version: CONSENT_POLICY_FALLBACK,
};

function isPrefs(value: unknown): value is ConsentPrefs {
  return (
    typeof value === "object" &&
    value !== null &&
    "essential" in value &&
    "analytics" in value &&
    "marketing" in value &&
    "version" in value
  );
}

/**
 * Read the persisted preferences. Returns ``null`` when no decision
 * has been made yet (banner should show); a populated object once the
 * user has saved preferences.
 *
 * Legacy v1 values are migrated lazily: ``"accepted"`` becomes
 * ``{essential: true, analytics: false, marketing: false}`` —
 * declined-by-default for the new categories. The user is NOT
 * re-prompted (that would be obnoxious), but they keep full control
 * via the in-app preferences page (PR 05 follow-up).
 */
export function readConsentPrefs(): ConsentPrefs | null {
  if (typeof window === "undefined") return null;
  try {
    const v2 = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (v2) {
      const parsed = JSON.parse(v2);
      if (isPrefs(parsed)) return parsed;
    }
    const v1 = window.localStorage.getItem(CONSENT_LEGACY_STORAGE_KEY);
    if (v1 === "accepted" || v1 === "declined") {
      return {
        ...DEFAULT_PREFS,
        ts: new Date().toISOString(),
      };
    }
  } catch {
    /* private-mode Safari etc. — treat as no decision yet */
  }
  return null;
}

/**
 * Read one category. Returns ``false`` if no decision has been made
 * (declined-by-default — data minimisation). ``essential`` always
 * returns ``true`` because those cookies don't need consent under
 * ePrivacy.
 */
export function getConsentCategory(category: ConsentCategory): boolean {
  if (category === "essential") return true;
  const prefs = readConsentPrefs();
  if (prefs === null) return false;
  return Boolean(prefs[category]);
}

/**
 * Persist preferences locally. Caller is responsible for the matching
 * server-side POST /consent calls.
 */
export function writeConsentPrefs(
  prefs: Omit<ConsentPrefs, "essential" | "version" | "ts">,
  policyVersion: string = CONSENT_POLICY_FALLBACK,
): ConsentPrefs {
  const full: ConsentPrefs = {
    essential: true,
    analytics: prefs.analytics,
    marketing: prefs.marketing,
    version: policyVersion,
    ts: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(full));
  } catch {
    /* swallow — UI flow continues even when storage is unavailable */
  }
  return full;
}

/**
 * Backend POST helper — fire-and-forget. We don't await the response
 * to keep the UI snappy; the audit trail is best-effort from the
 * client's perspective and authoritatively persisted from the
 * backend's. Caller can pass ``fetchFn`` for tests.
 */
export async function postConsent(
  category: Exclude<ConsentCategory, "essential">,
  granted: boolean,
  fetchFn: typeof fetch = fetch,
  policyVersion: string = CONSENT_POLICY_FALLBACK,
): Promise<void> {
  try {
    await fetchFn("/api/v1/consent", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        category,
        granted,
        policy_version: policyVersion,
      }),
    });
  } catch {
    /* network failure — server-side ledger may miss this entry,
       which is fine since the client-side localStorage is
       authoritative for FE behaviour */
  }
}
